import classnames from 'classnames';
import type {ReactNode} from 'react';
import React, {useState} from 'react';
import {createPortal} from 'react-dom';

import AnnotatablePassageSupplement from './AnnotatablePassageSupplement';
import AnnotationEditor from './AnnotationEditor';
import AnnotationSupplementNoteIndicator from './AnnotationSupplementNoteIndicator';
import {useAnnotatableContentPresence, useAnnotationModeStore} from './AnnotationModeStore';
import {buildSupplementAnchor} from './annotationAnchors';
import {isShieldExempt} from './annotationFreeze';
import {buildSupplementRegionId} from './annotationRegions';
import {ANNOTATE_IGNORE_PROPS} from './annotationTextProjection';
import {annotationAnchorAtPoint} from './annotationUtils';
import {
  SUPPLEMENT_ANNOTATABLE_CLASS,
  SUPPLEMENT_HOVER_BORDER_CLASS,
  SUPPLEMENT_MARKED_CLASS
} from './supplementCard';
import {useAnnotationGesture} from './useAnnotationGesture';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';
import {useSupplementCard} from './useSupplementCard';

import './annotations-cursor.scss';
import './annotations-highlights.scss';

/** The types annotation applies to at all; sortable-table stays host-region text. */
export const ANNOTATABLE_SUPPLEMENT_TYPES: ReadonlySet<string> = new Set([
  'image',
  'audio',
  'free-form',
  'passage'
]);

interface AnnotatableSupplementProps {
  supplementId: string;
  supplementType: string;
  /** Hosts whose supplements must not take marks (answer chips, ordering items). */
  disabled?: boolean;
  /** Language of the rendered supplement ('es' when translated); null = original. */
  lang?: string | null;
  children: ReactNode;
}

/**
 * Routes a supplement to the surface its type calls for (#18667): a passage takes text
 * marks, image, audio and free-form take one whole-supplement mark. Either way the
 * subtree leaves the host region, and its own region carries marks across questions.
 */
export default function AnnotatableSupplement({
  supplementId,
  supplementType,
  disabled = false,
  lang = null,
  children
}: AnnotatableSupplementProps) {
  // Static checks first, hookless: a chip's supplement must not even subscribe to
  // the availability gate (its flag query needs Apollo).
  if (disabled || !ANNOTATABLE_SUPPLEMENT_TYPES.has(supplementType)) {
    return <>{children}</>;
  }

  if (supplementType === 'passage') {
    return (
      <AnnotatablePassageSupplement supplementId={supplementId} lang={lang}>
        {children}
      </AnnotatablePassageSupplement>
    );
  }

  return (
    <AvailableAnnotatableSupplement supplementId={supplementId}>
      {children}
    </AvailableAnnotatableSupplement>
  );
}

function AvailableAnnotatableSupplement({
  supplementId,
  children
}: Pick<AnnotatableSupplementProps, 'supplementId' | 'children'>) {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();

  if (!isAnnotationModeAvailable) {
    return <>{children}</>;
  }

  return (
    <AnnotatableSupplementTarget supplementId={supplementId}>
      {children}
    </AnnotatableSupplementTarget>
  );
}

function AnnotatableSupplementTarget({
  supplementId,
  children
}: Pick<AnnotatableSupplementProps, 'supplementId' | 'children'>) {
  const regionId = buildSupplementRegionId(supplementId);
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const addAnnotation = useAnnotationModeStore((state) => state.addAnnotation);
  const {regionRef, region, annotations, cardClassName, notedMarks} = useSupplementCard(regionId);
  // At most one mark per supplement: the head of the bucket is the mark.
  const [mark] = annotations;
  const [isHovered, setIsHovered] = useState(false);
  const {activeAnnotation, handleMouseUp, closeEditor} = useAnnotationGesture({
    // Exempt supplement controls (zoom, expand, play) act; they don't mark.
    shouldYield: (event) => event.button !== 0 || isShieldExempt(event),
    // At most one mark per supplement: a click reopens it, or creates the first.
    resolveAtPoint: (event) =>
      annotationAnchorAtPoint(
        event.clientX,
        event.clientY,
        mark?.id ?? addAnnotation(buildSupplementAnchor(supplementId))
      ),
    consume: true
  });
  useAnnotatableContentPresence(true);

  return (
    <>
      <div
        {...ANNOTATE_IGNORE_PROPS}
        ref={regionRef}
        data-testid='annotatable-supplement'
        className={classnames(
          SUPPLEMENT_ANNOTATABLE_CLASS,
          cardClassName,
          isAnnotationModeOn && 'cursor-highlighter',
          isAnnotationModeOn &&
            !mark &&
            isHovered && [SUPPLEMENT_MARKED_CLASS, SUPPLEMENT_HOVER_BORDER_CLASS]
        )}
        onMouseUp={handleMouseUp}
        onMouseOver={(event) => setIsHovered(!isShieldExempt(event))}
        onMouseLeave={() => setIsHovered(false)}
      >
        {children}
      </div>
      <AnnotationSupplementNoteIndicator region={region} notedMarks={notedMarks} />
      {activeAnnotation &&
        // Portaled to body: mounting even the popover's fixed anchor span inside
        // the host region's container would churn its MutationObserver and flash
        // every text mark on open/close.
        createPortal(
          <AnnotationEditor
            regionId={regionId}
            activeAnnotation={activeAnnotation}
            onClose={closeEditor}
            allowUnderline={false}
          />,
          document.body
        )}
    </>
  );
}
